<!-- SubpopulationInsights.vue — Stats + HCC prevalence chart per subpopulation -->
<template>
  <div class="px-5 py-4 border-b border-gray-200 bg-white shrink-0">
    <!-- Row 1: Count + Stats -->
    <div class="flex items-start gap-6 flex-wrap">
      <div class="shrink-0">
        <div class="text-2xl font-bold text-gray-900">{{ count.toLocaleString() }}</div>
        <div class="text-xs text-gray-500">{{ label }} · {{ pct }}% of population</div>
      </div>
      <div class="h-12 w-px bg-gray-200 shrink-0"></div>
      <div class="flex gap-4 flex-wrap items-start">
        <div v-for="stat in stats" :key="stat.label" class="text-center min-w-[60px]">
          <div class="text-lg font-semibold" :class="stat.highlight ? 'text-red-600' : 'text-gray-900'">{{ stat.value }}</div>
          <div class="text-[10px] text-gray-500 leading-tight">{{ stat.label }}</div>
        </div>
      </div>
    </div>

    <!-- Row 2: HCC prevalence chart -->
    <div v-if="chartData.length" class="mt-3 pt-3 border-t border-gray-100">
      <div class="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">{{ chartTitle }}</div>
      <div class="border border-gray-200 rounded-lg overflow-hidden p-3">
        <div ref="chart" style="width: 100%;"></div>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';

const RACE_LABELS = { '1': 'White', '2': 'Black', '3': 'Other', '4': 'Asian/PI', '5': 'Hispanic', '6': 'Unknown' };
const GROUP_COLORS = ['#8b5cf6', '#3b82f6', '#0ea5e9', '#14b8a6', '#22c55e', '#f59e0b', '#ef4444'];

function getHCCs(p) {
  const raw = p.hcc_codes;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === 'string') {
    try { return JSON.parse(raw).map(String); } catch { return []; }
  }
  return [];
}

export default {
  name: 'SubpopulationInsights',
  props: {
    subpop: { type: String, required: true },
    patients: { type: Array, required: true },
    allPatients: { type: Array, required: true },
    hccLabels: { type: Object, default: () => ({}) },
  },

  computed: {
    count() { return this.patients.length; },
    label() {
      return { general: 'General Population', pediatric: 'Children & Adolescents', disabled: 'Members with Disabilities', smi: 'Serious Mental Illness', race: 'Racial & Ethnic Groups', lep: 'Limited English Proficiency', dual: 'Dual-Eligible Members', high_acuity: 'High-Acuity Complex', custom: 'Custom Subpopulations' }[this.subpop] || '';
    },
    pct() {
      if (!this.allPatients.length) return '0';
      return ((this.patients.length / this.allPatients.length) * 100).toFixed(1);
    },
    stats() {
      const pts = this.patients;
      if (!pts.length) return [];
      const avg = (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      const chronicVals = pts.filter(p => p.num_chronic !== null).map(p => p.num_chronic);
      const ageVals = pts.filter(p => p.age !== null).map(p => p.age);
      const edVals = pts.map(p => p.num_ed || 0);
      const ipVals = pts.map(p => p.num_ip || 0);
      const readmitCount = pts.filter(p => (p.num_readmit || 0) > 0).length;
      const genED = avg(this.allPatients.map(p => p.num_ed || 0));
      const base = [
        { label: 'Avg Age', value: avg(ageVals) !== null ? Math.round(avg(ageVals)) : '—' },
        { label: 'Avg Chronic', value: avg(chronicVals) !== null ? avg(chronicVals).toFixed(1) : '—' },
        { label: 'ED / Member', value: avg(edVals).toFixed(1) },
        { label: 'IP / Member', value: avg(ipVals).toFixed(1) },
        { label: 'Readmit %', value: (100 * readmitCount / pts.length).toFixed(1) + '%' },
      ];
      const extra = {
        general: [
          { label: 'Dual %', value: (100 * pts.filter(p => p.dual).length / pts.length).toFixed(1) + '%' },
          { label: 'Disabled %', value: (100 * pts.filter(p => p.disabled).length / pts.length).toFixed(1) + '%' },
        ],
        disabled: [
          { label: 'ESRD', value: pts.filter(p => p.esrd).length.toLocaleString() },
          { label: 'ED vs Gen', value: genED ? (avg(edVals) / genED).toFixed(1) + 'x' : '—', highlight: avg(edVals) > genED * 1.3 },
        ],
        smi: [
          { label: 'Dual %', value: (100 * pts.filter(p => p.dual).length / pts.length).toFixed(1) + '%' },
          { label: '3+ Chronic', value: (100 * pts.filter(p => (p.num_chronic || 0) >= 3).length / pts.length).toFixed(1) + '%' },
        ],
        lep: [{ label: 'Languages', value: new Set(pts.map(p => p.language).filter(Boolean)).size }],
        dual: [
          { label: 'Full', value: pts.filter(p => p.full_dual).length.toLocaleString() },
          { label: 'Partial', value: pts.filter(p => p.partial_dual && !p.full_dual).length.toLocaleString() },
        ],
        high_acuity: [
          { label: 'ED vs Gen', value: genED ? (avg(edVals) / genED).toFixed(1) + 'x' : '—', highlight: true },
        ],
        custom: [
          { label: 'Dual', value: pts.filter(p => p.dual).length.toLocaleString() },
          { label: 'High-Acuity', value: pts.filter(p => p.high_acuity).length.toLocaleString() },
          { label: 'Both', value: pts.filter(p => p.dual && p.high_acuity).length.toLocaleString() },
        ],
        pediatric: [
          { label: '0-5', value: pts.filter(p => p.age !== null && p.age <= 5).length.toLocaleString() },
          { label: '6-12', value: pts.filter(p => p.age >= 6 && p.age <= 12).length.toLocaleString() },
          { label: '13-17', value: pts.filter(p => p.age >= 13 && p.age <= 17).length.toLocaleString() },
        ],
      };
      return [...base, ...(extra[this.subpop] || [])];
    },

    chartTitle() {
      const m = {
        general: 'Top HCC Prevalence',
        disabled: 'Top HCC Prevalence — Disabled Members',
        smi: 'Top HCC Prevalence — SMI Members',
        race: 'Top HCC Prevalence by Race',
        lep: 'Top HCC Prevalence — LEP Members',
        dual: 'Top HCC Prevalence — Dual-Eligible',
        high_acuity: 'Top HCC Prevalence — High-Acuity',
        custom: 'Top HCC Prevalence by Custom Subpopulation',
        pediatric: 'Top HCC Prevalence — Pediatric',
      };
      return m[this.subpop] || 'Top HCC Prevalence';
    },

    chartData() {
      const pts = this.patients;
      if (!pts.length) return [];

      if (this.subpop === 'race') {
        return this._hccByGroup(pts, p => RACE_LABELS[String(p.race_cd)] || 'Unknown');
      }
      if (this.subpop === 'lep') {
        return this._hccByGroup(pts, p => p.language || 'Unknown');
      }
      if (this.subpop === 'custom') {
        return this._hccByGroup(pts, p => {
          if (p.high_acuity && p.dual) return 'Dual + High-Acuity';
          if (p.high_acuity) return 'High-Acuity';
          return 'Dual-Eligible';
        });
      }
      // All other subpops: simple HCC prevalence
      return this._hccPrevalence(pts);
    },
  },

  mounted() {
    this.$nextTick(() => this.renderChart());
  },

  methods: {
    hccDesc(code) {
      return this.hccLabels[String(code)] || '';
    },

    _hccPrevalence(pts) {
      const counts = {};
      pts.forEach(p => getHCCs(p).forEach(h => { counts[h] = (counts[h] || 0) + 1; }));
      const total = pts.length;
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([code, count]) => ({
          code,
          desc: this.hccDesc(code),
          value: count,
          pct: ((count / total) * 100).toFixed(1),
        }));
    },

    _hccByGroup(pts, groupFn) {
      // Get top 8 HCCs overall first
      const overall = {};
      pts.forEach(p => getHCCs(p).forEach(h => { overall[h] = (overall[h] || 0) + 1; }));
      const topCodes = Object.entries(overall).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([c]) => c);

      // Group patients
      const groups = {};
      pts.forEach(p => {
        const g = groupFn(p);
        if (!groups[g]) groups[g] = [];
        groups[g].push(p);
      });

      // Top 5 groups by size
      const topGroups = Object.entries(groups).sort((a, b) => b[1].length - a[1].length).slice(0, 5).map(([name]) => name);

      // Build grouped data: for each HCC, prevalence % per group
      return topCodes.map(code => {
        const row = { code, desc: this.hccDesc(code) };
        topGroups.forEach(g => {
          const gPts = groups[g];
          const count = gPts.filter(p => getHCCs(p).includes(code)).length;
          row[g] = ((count / gPts.length) * 100).toFixed(1);
        });
        return row;
      });
    },

    renderChart() {
      const el = this.$refs.chart;
      if (!el || !this.chartData.length) return;
      while (el.firstChild) el.removeChild(el.firstChild);

      if (this.subpop === 'race' || this.subpop === 'lep' || this.subpop === 'custom') {
        this._drawGroupedHBar(el);
      } else {
        this._drawSimpleHBar(el);
      }
    },

    _truncate(text, maxChars) {
      return text.length > maxChars ? text.slice(0, maxChars - 1) + '…' : text;
    },

    _drawSimpleHBar(el) {
      const data = this.chartData;
      const rowHeight = 10;
      const gap = 8;
      const codeWidth = 36;
      const descWidth = 280;
      const barStart = codeWidth + descWidth + 16;
      const h = data.length * (rowHeight + gap);
      const w = el.clientWidth;
      const barWidth = Math.max(w - barStart - 80, 60);

      const svg = d3.select(el).append('svg').attr('width', w).attr('height', h);
      const x = d3.scaleLinear().domain([0, d3.max(data, d => d.value) || 1]).range([0, barWidth]);

      data.forEach((d, i) => {
        const y = i * (rowHeight + gap);
        const cy = y + rowHeight / 2;
        // Row separator
        if (i > 0) {
          svg.append('line').attr('x1', 0).attr('x2', w).attr('y1', y - gap / 2).attr('y2', y - gap / 2)
            .attr('stroke', '#e5e7eb').attr('stroke-width', 1);
        }
        // Code
        svg.append('text').attr('x', 0).attr('y', cy)
          .attr('dominant-baseline', 'central')
          .attr('font-size', '11px').attr('font-weight', '600').attr('fill', '#6b7280').text(d.code);
        // Description
        svg.append('text').attr('x', codeWidth + 4).attr('y', cy)
          .attr('dominant-baseline', 'central')
          .attr('font-size', '11px').attr('fill', '#374151').text(this._truncate(d.desc || '—', 40));
        // Bar
        svg.append('rect').attr('x', barStart).attr('y', y).attr('width', x(d.value)).attr('height', rowHeight)
          .attr('fill', '#8b5cf6').attr('rx', 2).attr('opacity', 0.85);
        // Value
        svg.append('text').attr('x', barStart + x(d.value) + 6).attr('y', cy)
          .attr('dominant-baseline', 'central')
          .attr('font-size', '10px').attr('font-weight', '600').attr('fill', '#6b7280')
          .text(`${d.value.toLocaleString()} (${d.pct}%)`);
      });
    },

    _drawGroupedHBar(el) {
      const data = this.chartData;
      if (!data.length) return;

      const groups = Object.keys(data[0]).filter(k => k !== 'code' && k !== 'desc');
      const barHeight = 4;
      const groupGap = 1;
      const rowGap = 8;
      const rowHeight = groups.length * (barHeight + groupGap) + rowGap;
      const codeWidth = 36;
      const descWidth = 280;
      const barStart = codeWidth + descWidth + 16;
      const legendHeight = 16;
      const h = data.length * rowHeight + legendHeight;
      const w = el.clientWidth;
      const barWidth = Math.max(w - barStart - 60, 60);

      const maxVal = d3.max(data, d => d3.max(groups, g => +d[g])) || 1;
      const x = d3.scaleLinear().domain([0, maxVal]).range([0, barWidth]);
      const color = d3.scaleOrdinal().domain(groups).range(GROUP_COLORS);

      const svg = d3.select(el).append('svg').attr('width', w).attr('height', h);

      // Legend
      const lg = svg.append('g').attr('transform', `translate(${barStart}, 0)`);
      groups.forEach((grp, i) => {
        const lx = i * 80;
        lg.append('rect').attr('x', lx).attr('y', 0).attr('width', 8).attr('height', 8).attr('rx', 2).attr('fill', color(grp));
        lg.append('text').attr('x', lx + 12).attr('y', 8).attr('font-size', '9px').attr('fill', '#6b7280').text(grp);
      });

      data.forEach((d, ri) => {
        const ry = legendHeight + ri * rowHeight;
        const cy = ry + (groups.length * (barHeight + groupGap)) / 2;
        // Row separator
        if (ri > 0) {
          svg.append('line').attr('x1', 0).attr('x2', w).attr('y1', ry - rowGap / 2).attr('y2', ry - rowGap / 2)
            .attr('stroke', '#e5e7eb').attr('stroke-width', 1);
        }
        // Code
        svg.append('text').attr('x', 0).attr('y', cy)
          .attr('dominant-baseline', 'central')
          .attr('font-size', '11px').attr('font-weight', '600').attr('fill', '#6b7280').text(d.code);
        // Description
        svg.append('text').attr('x', codeWidth + 4).attr('y', cy)
          .attr('dominant-baseline', 'central')
          .attr('font-size', '11px').attr('fill', '#374151').text(this._truncate(d.desc || '—', 40));
        // Grouped bars
        groups.forEach((grp, gi) => {
          const by = ry + gi * (barHeight + groupGap);
          const val = +d[grp] || 0;
          svg.append('rect').attr('x', barStart).attr('y', by).attr('width', x(val)).attr('height', barHeight)
            .attr('fill', color(grp)).attr('rx', 1).attr('opacity', 0.85);
          svg.append('text').attr('x', barStart + x(val) + 4).attr('y', by + barHeight / 2)
            .attr('dominant-baseline', 'central')
            .attr('font-size', '8px').attr('fill', '#9ca3af').text(val + '%');
        });
      });
    },
  },
};
</script>
